import { useEffect, useMemo, useState } from 'react';
import {
  UserPlus, Users as UsersIcon, ShieldCheck, History, ShieldAlert,
  KeyRound, Check, Copy, MessageCircle, Mail, Phone, AlertTriangle,
  MoreHorizontal, RefreshCw, Eye, EyeOff, ArrowRight, FileDown, Zap, User, X,
} from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import {
  Avatar, Badge, Button, Card, Chip, EmptyState, Field, FilterMenu, Input, KV,
  Modal, SearchInput, SectionTag, Switch,
} from '../components/ui';
import {
  useAudit, useCreateUser, useResetUserPassword, useSections, useUpdateUser, useUsers,
} from '../api/queries';
import { downloadFile } from '../api/client';
import { isValidEmail, isValidPhone } from '../lib/validate';
import { fmtDate, fmtDateTime, initials, ROLE_LABEL, USER_STATUS_TONE, AUDIT_ACTION_META } from '../lib/format';
import type { CurrentUser, Rolle, UserListItem, AuditEntry } from '../api/types';
import { useToast } from '../lib/toast';
import { AuditIcon } from '../components/AuditIcon';

export function UsersScreen({ user }: { user: CurrentUser }) {
  const [tab, setTab] = useState<'users' | 'roles' | 'audit'>('users');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailUser, setDetailUser] = useState<UserListItem | null>(null);
  const [handoff, setHandoff] = useState<{ user: { vorname: string; nachname: string; email: string }; pw: string; title: string } | null>(null);

  const { data: usersList = [], isLoading: usersLoading } = useUsers();
  const admins = usersList.filter((u) => u.rolle === 'admin' && u.userStatus === 'aktiv');

  return (
    <div className="page-fade">
      <PageHeader
        title="Benutzer"
        subtitle={`${usersList.filter((u) => u.userStatus !== 'deaktiviert').length} aktive Konten · ${admins.length} Admins`}
        actions={<Button variant="primary" icon={<UserPlus size={16} />} onClick={() => setCreateOpen(true)}>Neuer Benutzer</Button>}
      />

      <div className="segmented" style={{ marginBottom: 18 }}>
        <button className={tab === 'users' ? 'active' : ''} onClick={() => setTab('users')} type="button">
          <UsersIcon size={14} /> Benutzer
        </button>
        <button className={tab === 'roles' ? 'active' : ''} onClick={() => setTab('roles')} type="button">
          <ShieldCheck size={14} /> Rollen & Rechte
        </button>
        <button className={tab === 'audit' ? 'active' : ''} onClick={() => setTab('audit')} type="button">
          <History size={14} /> Aktivitätsprotokoll
        </button>
      </div>

      {tab === 'users' && (
        <>
          {admins.length < 2 && (
            <div className="banner warn">
              <ShieldAlert size={18} />
              <div className="banner-text">Du bist aktuell der einzige Admin. Lege einen zweiten an, damit ihr euch gegenseitig helfen könnt.</div>
              <Button variant="secondary" size="sm" onClick={() => setCreateOpen(true)}>Zweiten Admin anlegen</Button>
            </div>
          )}
          <UsersTable users={usersList} isLoading={usersLoading} onSelect={(u) => setDetailUser(u)} />
        </>
      )}

      {tab === 'roles' && <RoleMatrix />}
      {tab === 'audit' && <AuditLogView currentUser={user} />}

      <CreateUserWizard
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(u, pw) => {
          setCreateOpen(false);
          setHandoff({ user: u, pw, title: `Login-Daten für ${u.vorname} ${u.nachname}` });
        }}
      />
      <UserDetailModal
        userRow={detailUser}
        currentUser={user}
        onClose={() => setDetailUser(null)}
        onReset={(u, pw) => {
          setDetailUser(null);
          setHandoff({ user: u, pw, title: `Neues Temp-Passwort für ${u.vorname} ${u.nachname}` });
        }}
      />
      <TempPasswordModal data={handoff} onClose={() => setHandoff(null)} />
    </div>
  );
}

// ----------------------------------------------------------------
function UsersTable({ users, onSelect, isLoading }: { users: UserListItem[]; onSelect: (u: UserListItem) => void; isLoading?: boolean }) {
  const { data: sections = [] } = useSections();
  const secMap = useMemo(() => new Map(sections.map((s) => [s.id, s])), [sections]);

  if (isLoading && users.length === 0) {
    return (
      <Card>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2, 3].map((i) => <div key={i} className="skeleton" style={{ height: 48 }} />)}
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="table-wrap scroll-area" style={{ maxHeight: 'calc(100vh - 320px)' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Benutzer</th>
              <th>Rolle</th>
              <th>Sektionen</th>
              <th>Recovery-Codes</th>
              <th>Letzter Login</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} onClick={() => onSelect(u)} style={{ cursor: 'pointer' }}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ position: 'relative' }}>
                      <Avatar name={initials(u.vorname, u.nachname)} />
                      {u.customPerms && (
                        <span title="abweichende Berechtigungen" style={{
                          position: 'absolute', bottom: -2, right: -2,
                          width: 12, height: 12, borderRadius: 999,
                          background: 'var(--bg-surface)', border: '2px solid #7c3aed',
                        }} />
                      )}
                    </span>
                    <div>
                      <div style={{ fontWeight: 500 }}>{u.vorname} {u.nachname}</div>
                      <div className="secondary-line t-mono" style={{ fontSize: 12 }}>{u.email}</div>
                    </div>
                  </div>
                </td>
                <td><RoleBadge rolle={u.rolle} /></td>
                <td>
                  {u.rolle === 'admin'
                    ? <span className="text-tertiary" style={{ fontSize: 12.5 }}>alle Sektionen</span>
                    : (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {u.sektionen.slice(0, 2).map((sId) => {
                          const sec = secMap.get(sId);
                          return sec ? <SectionTag key={sId} color={sec.color} name={sec.name} small /> : null;
                        })}
                        {u.sektionen.length > 2 && (
                          <span className="text-tertiary" style={{ fontSize: 11.5, alignSelf: 'center' }}>
                            +{u.sektionen.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                </td>
                <td>
                  {u.recoveryLeft != null ? (
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12.5,
                      color: u.recoveryLeft < 3 ? 'var(--warning)' : 'var(--text-secondary)', fontWeight: 500,
                    }}>
                      <KeyRound size={12} /> {u.recoveryLeft}/10
                    </span>
                  ) : <span className="text-quaternary">–</span>}
                </td>
                <td className="t-num secondary-line">{u.letzterLogin ? fmtDateTime(u.letzterLogin) : 'noch nie'}</td>
                <td>
                  <Badge tone={USER_STATUS_TONE[u.userStatus]} dot={u.userStatus === 'aktiv'}>{u.userStatus}</Badge>
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" icon={<MoreHorizontal size={14} />} onClick={() => onSelect(u)} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function RoleBadge({ rolle }: { rolle: Rolle }) {
  const tone = ({ admin: 'accent', sektionsleiter: 'info', stellvertreter: 'neutral', read_only: 'outline' } as const)[rolle];
  return <Badge tone={tone} dot={rolle === 'admin'}>{ROLE_LABEL[rolle]}</Badge>;
}

// ----------------------------------------------------------------
function RoleMatrix() {
  // Spalten: Admin · Sektionsleiter · Stellvertreter · Read-Only
  // Diese Tabelle muss exakt dem entsprechen, was der Server erzwingt
  // (auth.ts: canEdit = keine read_only, canDelete = nur admin,
  //  sectionIds = alle für admin und read_only).
  const rows: (string | boolean)[][] = [
    ['Alle Sektionen sehen', true, 'zugewiesene', 'zugewiesene', true],
    ['Mitglieder-Liste anzeigen', true, true, true, true],
    ['Mitglied anlegen / bearbeiten', true, true, true, false],
    ['Mitglied archivieren', true, true, true, false],
    ['Mitglied endgültig löschen (DSGVO)', true, false, false, false],
    ['Benutzer verwalten', true, false, false, false],
    ['Aktivitätsprotokoll', 'alle', 'eigene', 'eigene', 'eigene'],
    ['Statistik & CSV-Export', 'gesamt', 'eigene Sektion', 'eigene Sektion', 'gesamt'],
    ['Sektions-Einstellungen', true, false, false, false],
  ];
  return (
    <Card>
      <div className="card-header">
        <h3>Berechtigungs-Matrix</h3>
        <Badge tone="info" dot>durch API-Regeln erzwungen</Badge>
      </div>
      <div className="table-wrap scroll-area">
        <table className="tbl">
          <thead>
            <tr>
              <th>Aktion</th>
              {['Admin', 'Sektionsleiter', 'Stellvertreter', 'Read-Only'].map((r) => (
                <th key={r} style={{ textAlign: 'center', width: 130 }}>{r}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i}>
                <td style={{ fontWeight: 500 }}>{row[0]}</td>
                {row.slice(1).map((c, ci) => (
                  <td key={ci} style={{ textAlign: 'center' }}>
                    {c === true && <Check size={16} className="text-success" style={{ display: 'inline-flex' }} />}
                    {c === false && <span className="text-quaternary">—</span>}
                    {typeof c === 'string' && <Badge tone="info">{c}</Badge>}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------
function AuditLogView({ currentUser }: { currentUser: CurrentUser }) {
  const [q, setQ] = useState('');
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [userFilter, setUserFilter] = useState<string | null>(null);
  const { data: entries = [] } = useAudit({
    q: q || undefined,
    aktion: actionFilter ?? undefined,
    user: userFilter ?? undefined,
  });
  const { data: usersList = [] } = useUsers();
  const isAdmin = currentUser.rolle === 'admin';

  return (
    <Card>
      <div className="toolbar">
        <SearchInput value={q} onChange={setQ} placeholder="Ereignis, Benutzer, Datensatz…" />
        <FilterMenu
          label="Ereignis"
          icon={<Zap size={12} />}
          value={actionFilter}
          options={Object.entries(AUDIT_ACTION_META).map(([k, v]) => ({ value: k, label: v.label }))}
          onChange={setActionFilter}
        />
        {isAdmin && (
          <FilterMenu
            label="Benutzer"
            icon={<User size={12} />}
            value={userFilter}
            options={usersList.map((u) => ({ value: u.id, label: `${u.vorname} ${u.nachname}` }))}
            onChange={setUserFilter}
          />
        )}
        <div className="spacer" />
        <Button
          variant="secondary"
          size="sm"
          icon={<FileDown size={14} />}
          onClick={async () => {
            try { await downloadFile('/audit/export.csv', 'aktivitaetsprotokoll.csv'); }
            catch { /* ignore */ }
          }}
        >
          CSV
        </Button>
      </div>
      <div className="table-wrap scroll-area" style={{ maxHeight: 'calc(100vh - 360px)' }}>
        <table className="tbl">
          <thead>
            <tr><th>Zeitpunkt</th><th>Benutzer</th><th>Ereignis</th><th>Details</th><th>IP</th></tr>
          </thead>
          <tbody>
            {entries.map((l: AuditEntry) => {
              const meta = AUDIT_ACTION_META[l.aktion] ?? { label: l.aktion, tone: 'neutral' as const };
              return (
                <tr key={l.id} style={l.kritisch ? { background: 'var(--danger-soft)' } : undefined}>
                  <td className="t-num secondary-line">{fmtDateTime(l.timestamp)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Avatar name={initials(l.u_vorname ?? '?', l.u_nachname ?? '?')} size="sm" />
                      <span style={{ fontWeight: 500, fontSize: 13 }}>
                        {l.u_vorname ?? '–'} {l.u_nachname ?? ''}
                      </span>
                    </div>
                  </td>
                  <td><Badge tone={meta.tone} dot>{meta.label}</Badge></td>
                  <td>
                    <span style={{ fontSize: 13 }}>{l.feld}</span>{' '}
                    <span className="text-tertiary t-mono" style={{ fontSize: 11.5 }}>({l.tabelle})</span>
                  </td>
                  <td><span className="t-mono secondary-line" style={{ fontSize: 12 }}>{l.ip_adresse ?? '–'}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {entries.length === 0 && <EmptyState icon={<History size={20} />} title="Noch keine Einträge" />}
      </div>
    </Card>
  );
}

// ----------------------------------------------------------------
function CreateUserWizard({
  open, onClose, onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (user: { vorname: string; nachname: string; email: string; telefon: string }, pw: string) => void;
}) {
  const { data: sections = [] } = useSections();
  const create = useCreateUser();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const [rolle, setRolle] = useState<Rolle>('sektionsleiter');
  const [secs, setSecs] = useState<Set<string>>(new Set());
  const [form, setForm] = useState({ vorname: '', nachname: '', email: '', telefon: '' });

  useEffect(() => {
    if (open) { setStep(0); setRolle('sektionsleiter'); setSecs(new Set()); setForm({ vorname: '', nachname: '', email: '', telefon: '' }); }
  }, [open]);

  const toggleSec = (id: string) => setSecs((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const steps = ['Basis', 'Rolle', 'Sektionen', 'Berechtigungen', 'Bestätigung'];

  // Admin und Read-Only bekommen serverseitig ohnehin alle Sektionen;
  // nur diese beiden Rollen brauchen eine ausdrückliche Zuweisung.
  const needsSections = rolle === 'sektionsleiter' || rolle === 'stellvertreter';

  const finish = () => {
    create.mutate(
      { ...form, rolle, sektionen: rolle === 'admin' ? undefined : [...secs] },
      {
        onSuccess: (r) => onCreated(form, r.tempPasswort),
        onError: () => toast.push('Konnte Benutzer nicht anlegen', 'warn'),
      },
    );
  };

  return (
    <Modal
      open={open} onClose={onClose} title="Neuen Benutzer anlegen" size="lg"
      footer={
        <>
          {step > 0
            ? <Button variant="ghost" onClick={() => setStep((s) => s - 1)}>Zurück</Button>
            : <Button variant="ghost" onClick={onClose}>Abbrechen</Button>}
          <div style={{ flex: 1 }} />
          {step < 4
            ? <Button variant="primary" iconRight={<ArrowRight size={16} />} onClick={() => setStep((s) => s + 1)}
                disabled={
                  (step === 0 && (!form.vorname.trim() || !form.nachname.trim() || !isValidEmail(form.email) || (!!form.telefon && !isValidPhone(form.telefon))))
                  // Ohne Sektion sieht ein Sektionsleiter/Stellvertreter nach dem
                  // Login gar nichts — das wirkt wie ein Defekt. Also erzwingen.
                  || (step === 2 && needsSections && secs.size === 0)
                }>
                Weiter
              </Button>
            : <Button variant="primary" icon={<Check size={16} />} onClick={finish} disabled={create.isPending}>
                Anlegen & Temp-Passwort erzeugen
              </Button>}
        </>
      }
    >
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          {steps.map((s, i) => (
            <div key={i} style={{
              fontSize: 12, fontWeight: i === step ? 600 : 500,
              color: i === step ? 'var(--accent)' : i < step ? 'var(--text-secondary)' : 'var(--text-quaternary)',
              display: 'flex', gap: 4, alignItems: 'center',
            }}>
              {i < step && <Check size={11} />}{s}
            </div>
          ))}
        </div>
        <div className="progress-track"><div className="progress-fill" style={{ width: `${(step / 4) * 100}%` }} /></div>
      </div>

      <div style={{ minHeight: 230 }}>
        {step === 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="grid-2">
              <Field label="Vorname" required><Input value={form.vorname} onChange={(e) => setForm({ ...form, vorname: e.target.value })} placeholder="Hans" autoFocus /></Field>
              <Field label="Nachname" required><Input value={form.nachname} onChange={(e) => setForm({ ...form, nachname: e.target.value })} placeholder="Müller" /></Field>
            </div>
            <Field
              label="E-Mail"
              required
              error={form.email && !isValidEmail(form.email) ? 'Bitte gültige E-Mail eingeben.' : null}
            >
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="hans.mueller@gmail.com" className="t-mono" />
            </Field>
            <Field
              label="Telefon"
              hint="Für die Weitergabe des Temp-Passworts per WhatsApp/Telefon."
              error={form.telefon && !isValidPhone(form.telefon) ? 'Telefonnummer wirkt ungültig.' : null}
            >
              <Input value={form.telefon} onChange={(e) => setForm({ ...form, telefon: e.target.value })} placeholder="+43 …" className="t-mono" />
            </Field>
          </div>
        )}
        {step === 1 && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            {Object.entries(ROLE_LABEL).map(([id, label]) => {
              const desc: Record<string, string> = {
                admin: 'Sieht & verwaltet alles, inkl. Benutzer.',
                sektionsleiter: 'Verwaltet die zugewiesenen Sektionen.',
                stellvertreter: 'Wie Sektionsleiter, kann aber nicht endgültig löschen.',
                read_only: 'Sieht den ganzen Verein, ändert nichts.',
              };
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setRolle(id as Rolle)}
                  style={{
                    padding: 14, textAlign: 'left', borderRadius: 10,
                    border: `1px solid ${rolle === id ? 'var(--accent)' : 'var(--border-default)'}`,
                    background: rolle === id ? 'var(--accent-soft)' : 'var(--bg-surface)',
                    display: 'flex', flexDirection: 'column', gap: 4, cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      width: 14, height: 14, borderRadius: 999,
                      border: `2px solid ${rolle === id ? 'var(--accent)' : 'var(--border-strong)'}`,
                      background: rolle === id ? 'var(--accent)' : 'transparent',
                      boxShadow: rolle === id ? 'inset 0 0 0 2px var(--bg-surface)' : 'none',
                    }} />
                    <span style={{ fontWeight: 600, fontSize: 14, color: rolle === id ? 'var(--accent)' : 'var(--text-primary)' }}>{label}</span>
                  </div>
                  <span className="text-tertiary" style={{ fontSize: 12.5, lineHeight: 1.4 }}>{desc[id]}</span>
                </button>
              );
            })}
          </div>
        )}
        {step === 2 && (
          !needsSections
            ? <div className="muted-card" style={{ display: 'flex', gap: 10 }}><ShieldCheck size={16} className="text-accent" /><span style={{ fontSize: 13.5 }}>{rolle === 'admin' ? 'Admins' : 'Read-Only-Benutzer'} sehen automatisch alle Sektionen — keine Zuweisung nötig.</span></div>
            : (
              <Field
                label="Sektionen zuweisen"
                required
                hint="Mehrfachauswahl möglich."
                error={secs.size === 0 ? 'Mindestens eine Sektion wählen — sonst sieht die Person nach dem Login nichts.' : null}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {sections.map((s) => (
                    <Chip key={s.id} active={secs.has(s.id)} onClick={() => toggleSec(s.id)}>
                      <span className="sec-dot" style={{ background: s.color }} />{s.name}
                    </Chip>
                  ))}
                </div>
              </Field>
            )
        )}
        {step === 3 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <p className="text-tertiary" style={{ fontSize: 13, margin: 0 }}>
              Diese Rechte ergeben sich aus der gewählten Rolle <strong>{ROLE_LABEL[rolle]}</strong> und
              werden vom Server erzwungen. Sie sind pro Person nicht einzeln einstellbar —
              wähle bei Bedarf im Schritt „Rolle" eine andere Rolle.
            </p>
            {([
              ['Mitglieder sehen', true],
              ['Mitglieder anlegen und bearbeiten', rolle !== 'read_only'],
              // Archivieren hängt an requireWrite — also auch Stellvertreter.
              // Bankdaten bewusst nicht gelistet: „Finanzen vorerst ausblenden".
              ['Mitglieder archivieren', rolle !== 'read_only'],
              ['Mitglieder endgültig löschen (DSGVO)', rolle === 'admin'],
              ['Benutzer verwalten', rolle === 'admin'],
              ['Alle Sektionen sehen', rolle === 'admin' || rolle === 'read_only'],
            ] as [string, boolean][]).map(([label, granted]) => (
              <div
                key={label}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, padding: '9px 12px', background: 'var(--bg-muted)', borderRadius: 9,
                }}
              >
                <span style={{ fontSize: 13.5, color: granted ? undefined : 'var(--text-tertiary)' }}>
                  {label}
                </span>
                {granted
                  ? <Badge tone="success"><Check size={11} /> erlaubt</Badge>
                  : <Badge tone="neutral"><X size={11} /> nein</Badge>}
              </div>
            ))}
          </div>
        )}
        {step === 4 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Avatar name={initials(form.vorname || 'N', form.nachname || 'N')} size="lg" />
              <div>
                <div className="t-title-3">{form.vorname || 'Vorname'} {form.nachname || 'Nachname'}</div>
                <div style={{ marginTop: 4 }}><RoleBadge rolle={rolle} /></div>
              </div>
            </div>
            <div className="kv-list" style={{ background: 'var(--bg-muted)', borderRadius: 10, padding: '4px 14px' }}>
              <KV k="E-Mail"><span className="t-mono">{form.email || '—'}</span></KV>
              <KV k="Sektionen">{rolle === 'admin' ? 'alle' : ([...secs].map((id) => sections.find((s) => s.id === id)?.name).join(', ') || '—')}</KV>
            </div>
            <div className="banner info" style={{ margin: 0 }}>
              <KeyRound size={18} />
              <div className="banner-text">
                Beim Anlegen wird ein menschenlesbares Temp-Passwort erzeugt (z. B. <span className="t-mono">Adler-Wiese-7392</span>), das du gleich weitergeben kannst.
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ----------------------------------------------------------------
// UserInspector — Slide-Over von rechts (UX-konsistent zum Mitglied-Inspector)
// ----------------------------------------------------------------
function UserDetailModal({
  userRow, currentUser, onClose, onReset,
}: {
  userRow: UserListItem | null;
  currentUser: CurrentUser;
  onClose: () => void;
  onReset: (u: { vorname: string; nachname: string; email: string }, pw: string) => void;
}) {
  const { data: sections = [] } = useSections();
  const updateUser = useUpdateUser();
  const reset = useResetUserPassword();
  const toast = useToast();
  const open = !!userRow;

  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [telefon, setTelefon] = useState('');
  const [funktion, setFunktion] = useState('');
  const [rolle, setRolle] = useState<Rolle>('sektionsleiter');
  const [secs, setSecs] = useState<Set<string>>(new Set());
  const [confirmReset, setConfirmReset] = useState(false);

  useEffect(() => {
    if (userRow) {
      setVorname(userRow.vorname);
      setNachname(userRow.nachname);
      setTelefon(userRow.telefon ?? '');
      setFunktion(userRow.funktion ?? '');
      setRolle(userRow.rolle);
      setSecs(new Set(userRow.sektionen));
      setConfirmReset(false);
    }
  }, [userRow]);

  // ESC schließt
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const isSelf = !!userRow && userRow.id === currentUser.id;
  const isAdminUser = userRow?.rolle === 'admin';

  const toggleSec = (id: string) => {
    setSecs((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const buildPatch = (u: UserListItem): Record<string, unknown> => {
    const patch: Record<string, unknown> = {};
    if (vorname !== u.vorname) patch.vorname = vorname;
    if (nachname !== u.nachname) patch.nachname = nachname;
    if ((telefon || '') !== (u.telefon ?? '')) patch.telefon = telefon;
    if ((funktion || '') !== (u.funktion ?? '')) patch.funktion = funktion;
    if (rolle !== u.rolle) patch.rolle = rolle;
    const oldSec = new Set(u.sektionen);
    const diff = secs.size !== oldSec.size || [...secs].some((s) => !oldSec.has(s));
    if (diff) patch.sektionen = rolle === 'admin' ? [] : [...secs];
    return patch;
  };

  const save = () => {
    if (!userRow) return;
    const patch = buildPatch(userRow);
    if (Object.keys(patch).length === 0) { onClose(); return; }
    updateUser.mutate({ id: userRow.id, patch }, {
      onSuccess: () => { toast.push('Benutzer gespeichert'); onClose(); },
      onError: (e: any) => {
        if (e?.payload?.error === 'last_admin') {
          toast.push('Mindestens 2 aktive Admins erforderlich', 'warn');
        } else {
          toast.push('Konnte nicht speichern', 'warn');
        }
      },
    });
  };

  const setStatus = (status: 'aktiv' | 'deaktiviert') => {
    if (!userRow) return;
    updateUser.mutate({ id: userRow.id, patch: { user_status: status } }, {
      onSuccess: () => {
        toast.push(status === 'deaktiviert' ? `${userRow.vorname} deaktiviert` : `${userRow.vorname} reaktiviert`);
        onClose();
      },
      onError: (e: any) => {
        if (e?.payload?.error === 'last_admin') {
          toast.push('Letzten Admin kannst du nicht deaktivieren', 'warn');
        } else {
          toast.push('Konnte Status nicht ändern', 'warn');
        }
      },
    });
  };

  return (
    <>
      <div className={`inspector-overlay ${open ? 'show' : ''}`} onClick={onClose} />
      <div className={`inspector ${open ? 'show' : ''}`} role="dialog" aria-modal="true" aria-label={userRow ? `${userRow.vorname} ${userRow.nachname}` : undefined}>
        {userRow && (
          <>
            <div className="inspector-header">
              <div className="inspector-top-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Avatar name={initials(userRow.vorname, userRow.nachname)} size="lg" />
                  <div>
                    <div className="t-title-3">{userRow.vorname} {userRow.nachname}</div>
                    <div className="t-caption text-tertiary t-mono" style={{ marginTop: 2 }}>{userRow.email}</div>
                  </div>
                </div>
                <Button variant="ghost" size="sm" icon={<X size={16} />} onClick={onClose} title="Schließen" aria-label="Inspector schließen" />
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <RoleBadge rolle={userRow.rolle} />
                <Badge tone={USER_STATUS_TONE[userRow.userStatus]} dot={userRow.userStatus === 'aktiv'}>{userRow.userStatus}</Badge>
              </div>
            </div>

            <div className="inspector-body scroll-area">
              <div className="section-block" style={{ marginTop: 0 }}>
                <div className="section-block-title">Profil</div>
                <div className="grid-2">
                  <Field label="Vorname"><Input value={vorname} onChange={(e) => setVorname(e.target.value)} /></Field>
                  <Field label="Nachname"><Input value={nachname} onChange={(e) => setNachname(e.target.value)} /></Field>
                </div>
                <Field label="Telefon"><Input value={telefon} onChange={(e) => setTelefon(e.target.value)} className="t-mono" /></Field>
                <Field label="Funktion (Anzeige)" hint="z. B. Obmann, Kassier, Schriftführer">
                  <Input value={funktion} onChange={(e) => setFunktion(e.target.value)} />
                </Field>
              </div>

              <div className="section-block">
                <div className="section-block-title">Rolle</div>
                <Field label="Rolle">
                  <select className="input" value={rolle} onChange={(e) => setRolle(e.target.value as Rolle)} disabled={isSelf}>
                    {Object.entries(ROLE_LABEL).map(([id, label]) => (
                      <option key={id} value={id}>{label}</option>
                    ))}
                  </select>
                </Field>
                {isSelf && (
                  <div className="text-tertiary" style={{ fontSize: 12, marginTop: 6 }}>Deine eigene Rolle kannst du nicht ändern.</div>
                )}
              </div>

              {rolle !== 'admin' && rolle !== 'read_only' && (
                <div className="section-block">
                  <div className="section-block-title">Sektionen</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {sections.map((s) => (
                      <Chip key={s.id} active={secs.has(s.id)} onClick={() => toggleSec(s.id)}>
                        <span className="sec-dot" style={{ background: s.color }} />{s.name}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}

              <div className="section-block">
                <div className="section-block-title">Sicherheit</div>
                {!isSelf && (
                  confirmReset ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: 14, background: 'var(--warning-soft)', borderRadius: 10 }}>
                      <div style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--warning)' }}>
                        Passwort für {userRow.vorname} zurücksetzen?
                      </div>
                      <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                        {userRow.vorname} bekommt ein neues Temp-Passwort. Alle aktuellen Sitzungen werden beendet.
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button variant="ghost" size="sm" onClick={() => setConfirmReset(false)}>Abbrechen</Button>
                        <Button variant="primary" size="sm" icon={<KeyRound size={14} />} onClick={() => {
                          reset.mutate(userRow.id, {
                            onSuccess: (r) => onReset(userRow, r.tempPasswort),
                            onError: () => toast.push('Reset fehlgeschlagen', 'warn'),
                          });
                        }}>Zurücksetzen</Button>
                      </div>
                    </div>
                  ) : (
                    <Button variant="secondary" icon={<KeyRound size={14} />} onClick={() => setConfirmReset(true)}>Passwort zurücksetzen</Button>
                  )
                )}
                {isAdminUser && (
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-muted)', borderRadius: 9 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13.5 }}>
                      <KeyRound size={15} className={(userRow.recoveryLeft ?? 0) < 3 ? 'text-warning' : 'text-tertiary'} />
                      Recovery-Codes verbleibend: <strong>{userRow.recoveryLeft}/10</strong>
                    </div>
                  </div>
                )}
                {isSelf && (
                  <div className="text-tertiary" style={{ fontSize: 12.5, marginTop: 8 }}>
                    Dein eigenes Passwort änderst du unter „Einstellungen → Sicherheit".
                  </div>
                )}
              </div>

              {!isSelf && (
                <div className="section-block">
                  <div className="section-block-title">Status</div>
                  {userRow.userStatus !== 'deaktiviert' ? (
                    <Button
                      variant="ghost"
                      icon={<EyeOff size={14} />}
                      className="text-danger"
                      onClick={() => setStatus('deaktiviert')}
                    >
                      Benutzer deaktivieren
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      icon={<RefreshCw size={14} />}
                      onClick={() => setStatus('aktiv')}
                    >
                      Benutzer reaktivieren
                    </Button>
                  )}
                </div>
              )}
            </div>

            <div style={{ padding: 14, borderTop: '1px solid var(--border-subtle)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="ghost" size="sm" onClick={onClose}>Abbrechen</Button>
              <Button
                variant="primary"
                size="sm"
                icon={<Check size={14} />}
                onClick={save}
                disabled={updateUser.isPending}
              >
                Änderungen speichern
              </Button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ----------------------------------------------------------------
function TempPasswordModal({
  data, onClose,
}: {
  data: { user: { vorname: string; nachname: string; email: string }; pw: string; title: string } | null;
  onClose: () => void;
}) {
  if (!data) return null;
  const { user, pw, title } = data;
  // Die echte Adresse dieser Installation — nicht hartkodieren, sonst schickt man
  // neue Benutzer auf eine Domain, die es (noch) nicht gibt.
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const url = origin.replace(/^https?:\/\//, '');
  const message = `Login Mitgliederverwaltung\n${origin}\nE-Mail: ${user.email}\nTemp-Passwort: ${pw}\n\nBitte beim ersten Login ein eigenes Passwort festlegen.`;
  const wa = `https://wa.me/?text=${encodeURIComponent(message)}`;
  const mail = `mailto:${user.email}?subject=${encodeURIComponent('Dein Zugang zur Mitgliederverwaltung')}&body=${encodeURIComponent(message)}`;
  return (
    <Modal open={!!data} onClose={onClose} title={title}
      footer={<Button variant="primary" onClick={onClose}>Schließen</Button>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div className="muted-card" style={{ display: 'grid', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span className="t-mono">{url}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <span className="t-mono">{user.email}</span>
          </div>
        </div>
        <div>
          <div className="field-label" style={{ marginBottom: 6 }}>Temporäres Passwort</div>
          <div className="temp-pw">
            <span className="val">
              {pw.split('').map((ch, i) => (
                <span className="ch" key={i} style={{ animationDelay: `${i * 28}ms` }}>{ch}</span>
              ))}
            </span>
            <button className="copy" onClick={() => navigator.clipboard?.writeText(pw)} title="Kopieren">
              <Copy size={17} />
            </button>
          </div>
        </div>
        <div className="banner warn" style={{ margin: 0 }}>
          <AlertTriangle size={18} />
          <div className="banner-text">
            {user.vorname} muss das Passwort beim ersten Login ändern. Es wird nur jetzt angezeigt.
          </div>
        </div>
        <div>
          <div className="field-label" style={{ marginBottom: 8 }}>Weitergeben</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="secondary" icon={<Copy size={16} />} onClick={() => navigator.clipboard?.writeText(message)}>
              Alle Daten kopieren
            </Button>
            <a href={wa} target="_blank" rel="noreferrer" className="btn btn-secondary">
              <MessageCircle size={16} /> Per WhatsApp
            </a>
            <a href={mail} className="btn btn-secondary">
              <Mail size={16} /> Per E-Mail
            </a>
          </div>
        </div>
      </div>
    </Modal>
  );
}
